import React from "react";
import {
  DraftCircle,
  DropdownDots,
  LiveCircle,
  ScheduledCircle,
  SortIcon,
} from "../../components/ui/icons";
import TruncatedText from "../../components/ui/truncatedText";
import {
  calculateTimeLeft,
  calculateStatus,
  AlertStatus,
  formatGroupHierarchyLabel,
} from "./utils";
import { IAlertData, IGroupAlerts } from "../../common/types/alertsTypes";
import {
  SEVERITY_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
} from "../../common/constants";
import { ChevronDown, ChevronRight, CircleEllipsis } from "lucide-react";

const getAlertDetail = (rowData: any) => {
  if (Array.isArray(rowData?.alertDetails)) {
    return rowData.alertDetails[0] || {};
  }

  return rowData?.alertDetails || rowData?.parentAlertDetails || {};
};

const getStatusClassName = (status: string) =>
  status.toLowerCase().replace(/\s+/g, "-");

const getRowStatus = (rowData: any): string => {
  if (rowData?.isChildRow) {
    return rowData?.groupStatus || "";
  }

  if (rowData?.aggregateStatus) {
    return rowData.aggregateStatus;
  }

  const alertDetail = getAlertDetail(rowData);

  return alertDetail?.startDateTime
    ? calculateStatus(
        alertDetail.startDateTime,
        alertDetail.endDateTime,
        rowData?.alertStatus,
        rowData?.failedDevices || [],
        rowData?.successDevices || [],
      )
    : "";
};

const getSeverityLabel = (severity?: string): string => {
  if (!severity) {
    return "";
  }

  if (severity === "emergency") {
    return "High";
  }

  return severity.charAt(0).toUpperCase() + severity.slice(1);
};

const getGroupDisplayName = (group: any): string => {
  return (
    group?.groupDisplayName ||
    formatGroupHierarchyLabel(
      group?.groupHierarchy || group?.hierarchy,
      group?.groupName || group?.displayName || "",
    )
  );
};

const getSelectedGroupsFromAlertRow = (rowData: any): IGroupAlerts[] => {
  const rowGroups = Array.isArray(rowData?.groups) ? rowData.groups : [];
  const selectedGroupsMap = new Map<string, IGroupAlerts>();

  rowGroups.forEach((group) => {
    const groupId = group?.id || group?.groupId;
    const displayName = getGroupDisplayName(group);

    if (!groupId || !displayName || group?.isSearchPlaceholderRow) {
      return;
    }

    if (!selectedGroupsMap.has(groupId)) {
      selectedGroupsMap.set(groupId, {
        id: groupId,
        displayName,
      });
    }
  });

  if (!selectedGroupsMap.size && rowData?.groupId) {
    selectedGroupsMap.set(rowData.groupId, {
      id: rowData.groupId,
      displayName: rowData.groupName || "",
    });
  }

  return Array.from(selectedGroupsMap.values());
};

const getLegacyAlertData = (rowData: any): IAlertData => {
  const selectedGroups = getSelectedGroupsFromAlertRow(rowData);
  const firstSelectedGroup = selectedGroups[0];
  const firstGroup = rowData?.groups?.[0];
  const alertDetail = getAlertDetail(rowData);

  return {
    ...rowData,
    _id: rowData.alertId,
    alertDetails: [alertDetail],

    groupId:
      firstSelectedGroup?.id || firstGroup?.groupId || rowData?.groupId || "",
    groupName:
      firstSelectedGroup?.displayName ||
      firstGroup?.groupName ||
      rowData?.groupName ||
      "",

    groups: selectedGroups,

    templateId: firstGroup?.templateId || rowData?.templateId || 0,
    jobId:
      firstGroup?.jobId || firstGroup?.groupAlertId || rowData?.jobId || "",
    __v: rowData?.__v || 0,
  } as IAlertData & { groups: IGroupAlerts[] };
};

const getAffectedGroupNamesFromGroupRow = (groupRow: any): string[] => {
  const affectedGroupNamesSet = new Set<string>();

  const collectGroupNames = (currentGroupRow: any) => {
    if (!currentGroupRow || currentGroupRow?.isSearchPlaceholderRow) {
      return;
    }

    const status = currentGroupRow?.groupStatus || "";
    const isLive = status.toLowerCase() === AlertStatus.LIVE.toLowerCase();
    const isPartiallyLive = status.toLowerCase() === AlertStatus.PARTIALLY_LIVE.toLowerCase();

    if (isLive || isPartiallyLive) {
      const groupName = getGroupDisplayName(currentGroupRow);

      if (groupName) {
        affectedGroupNamesSet.add(groupName);
      }
    }

    if (Array.isArray(currentGroupRow?.subRows)) {
      currentGroupRow.subRows.forEach(collectGroupNames);
    }
  };

  collectGroupNames(groupRow);

  return Array.from(affectedGroupNamesSet);
};

const getAffectedGroupNamesFromAlertRow = (alertRow: any): string[] => {
  const affectedGroupNamesSet = new Set<string>();

  if (Array.isArray(alertRow?.groups)) {
    alertRow.groups.forEach((group) => {
      if (group?.isSearchPlaceholderRow) {
        return;
      }

      const status = group?.groupStatus || "";
      const isLive = status.toLowerCase() === AlertStatus.LIVE.toLowerCase();
      const isPartiallyLive = status.toLowerCase() === AlertStatus.PARTIALLY_LIVE.toLowerCase();

      if (isLive || isPartiallyLive) {
        const groupName = getGroupDisplayName(group);

        if (groupName) {
          affectedGroupNamesSet.add(groupName);
        }
      }
    });
  }

  if (!affectedGroupNamesSet.size && alertRow?.groupName) {
    const status = getRowStatus(alertRow) || "";
    const isLive = status.toLowerCase() === AlertStatus.LIVE.toLowerCase();
    const isPartiallyLive = status.toLowerCase() === AlertStatus.PARTIALLY_LIVE.toLowerCase();

    if (isLive || isPartiallyLive) {
      affectedGroupNamesSet.add(alertRow.groupName);
    }
  }

  return Array.from(affectedGroupNamesSet);
};

const AlertDetailsPlaceholder = ({
  onClick,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <button
    type="button"
    className="alert-details-placeholder"
    title="Click to load alert details"
    onMouseDown={(event) => {
      event.preventDefault();
      event.stopPropagation();
    }}
    onClick={onClick}
  >
    <CircleEllipsis size={15} strokeWidth={2.2} />
    <span>View details</span>
  </button>
);

const AlertsColumns = ({
  alertGroupOptions,
  canManageAlerts,
  onEditAlert,
  isGlobalSearchActive,
  onLoadSearchAlertDetails,
  onOpenEndAlertDialog,
  openAlertActionDropdownId,
  onToggleAlertActionDropdown,
  onCloseAlertActionDropdown,
  onOpenDeleteAlertDialog,
  onOpenFailedDevicesDialog,
}: {
  alertGroupOptions: string[];
  canManageAlerts: boolean;
  onEditAlert: (alertData: IAlertData) => void;
  isGlobalSearchActive: boolean;
  onLoadSearchAlertDetails: (
    alertId: string,
    currentAlertRow?: any,
  ) => Promise<boolean>;
  onOpenEndAlertDialog: (payload: {
    alertId?: string;
    alertHistoryId?: string;
    alertMessage: string;
    actionLabel: string;
    affectedGroupNames?: string[];
  }) => void;
  onOpenDeleteAlertDialog: (payload: {
    alertId: string;
    jobId: string;
    alertMessage: string;
    groupNames: string[];
  }) => void;
  openAlertActionDropdownId: string | null;
  onToggleAlertActionDropdown: (dropdownId: string) => void;
  onCloseAlertActionDropdown: () => void;
  onOpenFailedDevicesDialog: (deviceIds: string[]) => void;
}) => {
  return [
    {
      accessorFn: (row: any) => getRowStatus(row),
      id: "status",
      header: () => (
        <div className="table-heading">
          Status{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      meta: {
        options: STATUS_OPTIONS,
      },
      cell: (row: any) => {
        const rowData = row?.row?.original;
        const status = String(row.getValue() || "");
        const statusClassName = getStatusClassName(status);

        const isSearchParentRowWaitingForDetails =
          isGlobalSearchActive &&
          rowData?.isParentRow &&
          !rowData?.isSearchDetailsLoaded;

        const handleLoadAlertDetailsAndExpand = async (
          event: React.MouseEvent<HTMLButtonElement>,
        ) => {
          event.preventDefault();
          event.stopPropagation();

          if (
            isGlobalSearchActive &&
            rowData?.isParentRow &&
            !rowData?.isSearchDetailsLoaded
          ) {
            const isLoaded = await onLoadSearchAlertDetails(
              rowData.alertId,
              rowData,
            );

            if (!isLoaded) {
              return;
            }
          }

          row.row.toggleExpanded();
        };

        const isLiveAlert: boolean =
          status.toLowerCase() === AlertStatus.LIVE.toLowerCase();
        const isPastAlert: boolean =
          status.toLowerCase() === AlertStatus.PAST.toLowerCase();
        const isScheduledAlert: boolean =
          status.toLowerCase() === AlertStatus.SCHEDULED.toLowerCase();
        const isFailedAlert: boolean =
          status.toLowerCase() === AlertStatus.FAILED.toLowerCase();
        const isPartiallyLiveAlert: boolean =
          status.toLowerCase() === AlertStatus.PARTIALLY_LIVE.toLowerCase();

        const areFailedDevices: boolean = isFailedAlert || isPartiallyLiveAlert;
        const failedDeviceNames: string[] =
          rowData?.failedDeviceDetails || rowData?.failedDevices || [];

        return (
          <div className="table-cell">
            <div
              className={`status ${rowData?.isChildRow ? "child-status-cell" : ""}`}
            >
              {row.row.getCanExpand() ? (
                <button
                  type="button"
                  className="expand-alert-button"
                  aria-label={
                    row.row.getIsExpanded()
                      ? "Collapse alert row"
                      : "Expand alert row"
                  }
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleLoadAlertDetailsAndExpand}
                >
                  {row.row.getIsExpanded() ? (
                    <ChevronDown size={16} strokeWidth={2.2} />
                  ) : (
                    <ChevronRight size={16} strokeWidth={2.2} />
                  )}
                </button>
              ) : (
                <span className="expand-alert-placeholder" />
              )}

              {isSearchParentRowWaitingForDetails ? (
                <AlertDetailsPlaceholder
                  onClick={handleLoadAlertDetailsAndExpand}
                />
              ) : (
                <>
                  {isLiveAlert && <LiveCircle />}
                  {isPastAlert && <DraftCircle />}
                  {isScheduledAlert && <ScheduledCircle />}

                  <span className={statusClassName}>{status}</span>
                </>
              )}
            </div>
          </div>
        );
      },
      footer: (props: any) => props.column.id,
    },
    {
      accessorFn: (row: any) => {
        if (row?.isChildRow) {
          return "";
        }

        const alertDetail = getAlertDetail(row);
        return getSeverityLabel(alertDetail?.severity);
      },
      accessorKey: "severity",
      header: () => (
        <div className="table-heading">
          Severity{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      meta: {
        options: SEVERITY_OPTIONS,
      },
      footer: (props: any) => props.column.id,
      cell: (row: any) => {
        const value = String(row.getValue() || "");

        return (
          <div className="table-cell">
            <div className={value.toLowerCase()}>{value}</div>
          </div>
        );
      },
    },
    {
      accessorFn: (row: any) => {
        if (row?.isChildRow) {
          return "";
        }

        const alertDetail = getAlertDetail(row);
        return alertDetail?.source || "";
      },
      accessorKey: "source",
      header: () => (
        <div className="table-heading">
          Source{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      meta: {
        options: SOURCE_OPTIONS,
      },
      footer: (props: any) => props.column.id,
      cell: (row: any) => (
        <div className="table-cell">
          <div>{row.getValue()}</div>
        </div>
      ),
    },
    {
      accessorFn: (row: any) => {
        if (row?.isChildRow) {
          return "";
        }

        const alertDetail = getAlertDetail(row);
        return alertDetail?.alertMessage || "";
      },
      accessorKey: "alertMessage",
      header: () => (
        <div className="table-heading">
          Alert Message{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      footer: (props: any) => props.column.id,
      enableColumnFilter: false,
      cell: (row: any) => (
        <div className="table-cell">
          <div>
            <TruncatedText text={String(row.getValue() || "")} maxLength={30} />
          </div>
        </div>
      ),
    },
    {
      accessorFn: (row: any) => {
        if (row?.isChildRow) {
          return "";
        }

        const alertDetail = getAlertDetail(row);

        return alertDetail?.startDateTime && alertDetail?.endDateTime
          ? calculateTimeLeft(
              alertDetail.startDateTime,
              alertDetail.endDateTime,
              row?.alertStatus,
            )
          : "";
      },
      accessorKey: "timeLeft",
      header: () => (
        <div className="table-heading">
          Time Left{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      footer: (props: any) => props.column.id,
      enableColumnFilter: false,
      cell: (row: any) => <div className="table-cell">{row.getValue()}</div>,
    },
    {
      accessorFn: (row: any) => {
        if (row?.isChildRow) {
          return row?.groupName || "";
        }

        return row?.groupCountLabel || row?.groupName || "";
      },
      accessorKey: "groupName",
      header: () => (
        <div className="table-heading">
          Groups{" "}
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      meta: {
        options: alertGroupOptions,
      },
      footer: (props: any) => props.column.id,
      cell: (row: any) => {
        const rowData = row?.row?.original;
        const indentation = Math.max(row.row.depth - 1, 0) * 24;

        const isSearchParentRowWaitingForDetails =
          isGlobalSearchActive &&
          rowData?.isParentRow &&
          !rowData?.isSearchDetailsLoaded;

        const handleLoadAlertDetailsAndExpand = async (
          event: React.MouseEvent<HTMLButtonElement>,
        ) => {
          event.preventDefault();
          event.stopPropagation();

          if (
            isGlobalSearchActive &&
            rowData?.isParentRow &&
            !rowData?.isSearchDetailsLoaded
          ) {
            const isLoaded = await onLoadSearchAlertDetails(
              rowData.alertId,
              rowData,
            );

            if (!isLoaded) {
              return;
            }
          }

          row.row.toggleExpanded();
        };

        if (rowData?.isChildRow) {
          if (rowData?.isSearchPlaceholderRow) {
            return <div className="table-cell"></div>;
          }

          const groupDisplayName = getGroupDisplayName(rowData);

          return (
            <div
              className="table-cell alert-group-child-name"
              style={{ paddingLeft: `${indentation}px` }}
              title={groupDisplayName}
            >
              <span className="child-branch">└</span>
              <span className="group-hierarchy-label">{groupDisplayName}</span>
            </div>
          );
        }

        if (isSearchParentRowWaitingForDetails) {
          return (
            <div className="table-cell groups-placeholder-cell">
              <AlertDetailsPlaceholder
                onClick={handleLoadAlertDetailsAndExpand}
              />
            </div>
          );
        }

        return <div className="table-cell">{row.getValue()}</div>;
      },
    },
    {
      accessorFn: (row: any) => getRowStatus(row),
      accessorKey: "actions",
      header: () => <span></span>,
      footer: (props: any) => props.column.id,
      enableColumnFilter: false,
      cell: (row: any) => {
        const rowData = row?.row?.original;
        const status = String(row.getValue() || "");
        const alertDetail = getAlertDetail(rowData);

        const alertId: string = rowData?.alertId;
        const alertHistoryId: string = rowData?.alertHistoryId || rowData?._id;
        const alertMessage: string = alertDetail?.alertMessage || "";

        const alertData: IAlertData = getLegacyAlertData(rowData);
        const jobId: string = rowData?.jobId || "";
        const groupNames: string[] = getAffectedGroupNamesFromAlertRow(rowData);

        const failedDeviceNames: string[] =
          rowData?.failedDeviceDetails || rowData?.failedDevices || [];

        const isLiveAlert: boolean =
          status.toLowerCase() === AlertStatus.LIVE.toLowerCase();
        const isPartiallyLiveAlert: boolean =
          status.toLowerCase() === AlertStatus.PARTIALLY_LIVE.toLowerCase();
        const isPastAlert: boolean =
          status.toLowerCase() === AlertStatus.PAST.toLowerCase();
        const isScheduledAlert: boolean =
          status.toLowerCase() === AlertStatus.SCHEDULED.toLowerCase();
        const isFailedAlert: boolean =
          status.toLowerCase() === AlertStatus.FAILED.toLowerCase();
        const areFailedDevices: boolean = isPartiallyLiveAlert || isFailedAlert;
        const isPendingAlert: boolean =
          status.toLowerCase() === AlertStatus.PENDING.toLowerCase();

        if (!canManageAlerts) {
          return <div className="actions-btns" />;
        }

        const isSearchParentRowWaitingForDetails =
          isGlobalSearchActive &&
          rowData?.isParentRow &&
          !rowData?.isSearchDetailsLoaded;

        if (isSearchParentRowWaitingForDetails) {
          return (
            <div
              className="alert-actions-cell parent-action-cell"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            />
          );
        }

        if (rowData?.isChildRow) {
          const dropdownId = `${alertId}-${alertHistoryId}-${row.row.id}`;

          return (
            <div
              className="alert-actions-cell group-action-cell"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {(isLiveAlert || isPartiallyLiveAlert) && (
                <button
                  type="button"
                  className="end-group-button"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();

                    onOpenEndAlertDialog({
                      alertHistoryId,
                      alertMessage,
                      actionLabel: "End Alert",
                      affectedGroupNames:
                        getAffectedGroupNamesFromGroupRow(rowData),
                    });
                  }}
                >
                  End Alert
                </button>
              )}

              {areFailedDevices && failedDeviceNames.length > 0 && (
                <div
                  className="alert-action-dropdown"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="alert-action-dropdown-toggle"
                    aria-label="Alert actions"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleAlertActionDropdown(dropdownId);
                    }}
                  >
                    <DropdownDots />
                  </button>

                  {openAlertActionDropdownId === dropdownId && (
                    <div
                      className="alert-action-dropdown-menu"
                      role="menu"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="alert-action-dropdown-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          onCloseAlertActionDropdown();
                          onOpenFailedDevicesDialog(failedDeviceNames);
                        }}
                      >
                        Show Devices
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            className="alert-actions-cell parent-action-cell"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {(isLiveAlert || isPartiallyLiveAlert) && (
              <button
                type="button"
                className="end-all-button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();

                  onOpenEndAlertDialog({
                    alertId,
                    alertMessage,
                    actionLabel: "End All",
                    affectedGroupNames: groupNames,
                  });
                }}
              >
                End All
              </button>
            )}

            {!isPastAlert &&
              !isPendingAlert &&
              isScheduledAlert &&
              (() => {
                const dropdownId = `${alertId}-${row.row.id}`;

                return (
                  <div
                    className="alert-action-dropdown"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="alert-action-dropdown-toggle"
                      aria-label="Alert actions"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleAlertActionDropdown(dropdownId);
                      }}
                    >
                      <DropdownDots />
                    </button>

                    {openAlertActionDropdownId === dropdownId && (
                      <div
                        className="alert-action-dropdown-menu"
                        role="menu"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="alert-action-dropdown-item"
                          onClick={(event) => {
                            event.stopPropagation();
                            onCloseAlertActionDropdown();
                            onEditAlert(alertData);
                          }}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="alert-action-dropdown-item"
                          onClick={(event) => {
                            event.stopPropagation();
                            onCloseAlertActionDropdown();
                            onOpenDeleteAlertDialog({
                              alertId,
                              jobId,
                              alertMessage,
                              groupNames,
                            });
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
          </div>
        );
      },
    },
  ];
};

export default AlertsColumns;
